<template>
  <div :class="[$style.container, 'col-sm-5']" data-testid="price-value-hg">
    <HgCardFlat>
      <div v-if="isLoading" :class="$style.loadingWrapper" data-testid="price-value-skeleton">
        <HgSkeleton height="30px" width="220px" />
        <HgSkeleton height="20px" width="120px" />
        <HgSkeleton height="100px" />
      </div>
      <template v-else>
        <h3 :class="$style.heading">{{ $t('localLanding.priceValue.title') }}</h3>
        <p v-if="!priceData.price" :class="$style.noDataText">
          {{ $t('localLanding.priceDevelopment.noData') }}
        </p>
        <template v-else>
          <div :class="$style.date">
            {{
              $t('localLanding.priceValue.date', {
                date: priceData.date,
              })
            }}
          </div>
          <div :class="$style.priceContentWrapper">
            <p :class="$style.price">
              {{ priceData.price }} CHF/<span>m<sup>2</sup></span>
            </p>
            <div :class="$style.rangeBar">
              <div :class="$style.rangeBarInner" />
            </div>
            <div :class="$style.rangeTextWrapper">
              <span>{{ priceData.range.min }} CHF/m<sup>2</sup></span>
              <span>{{ priceData.range.max }} CHF/m<sup>2</sup></span>
            </div>
          </div>
        </template>
      </template>
    </HgCardFlat>
  </div>
</template>

<script setup lang="ts">
import adaptPriceData, {
  getDefaultPriceValue,
  MedianPriceDataItem,
} from '@/components/local-landing-page/PriceDevelopment/adaptPriceData';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { HgCardFlat, HgSkeleton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Composables
const localLandingStore = useLocalLandingStore();
const { t, locale } = useI18n();
const {
  propertyType,
  priceData: rawPriceData,
  isPriceDataLoading,
  isResolvingLocation,
} = storeToRefs(localLandingStore);

// Reactive data
const priceData = ref<MedianPriceDataItem>(getDefaultPriceValue());

// Other computed properties
const isLoading = computed(() => {
  return isResolvingLocation.value || isPriceDataLoading.value;
});

// Methods
const handlePriceData = () => {
  const data = adaptPriceData(rawPriceData.value.price, true, locale.value as HgSupportedLanguage);
  priceData.value = data[propertyType.value];
};

// Watchers
watch(locale, handlePriceData);
watch(propertyType, handlePriceData);
watch(rawPriceData, handlePriceData, { immediate: true });
</script>

<style module lang="scss">
@use '@/assets/variables' as *;

.container {
  height: inherit;
  color: $color-grey-100;

  &[class*='col-'] {
    padding: 0;
  }

  [class*='HgCardFlat_cardFlat'] {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: inherit;
    padding: $space-100 $space-150;
    border: unset;
    box-shadow: $elevation-minimal;
  }
}

.loadingWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  width: 100%;
  height: 100%;

  > span:last-child {
    margin-top: $space-200;
  }
}

.noDataText {
  align-self: center;
  color: $color-grey-90;

  @include grid-media-query(sm) {
    margin-bottom: $space-600;
  }

  @include body-3;
}

.heading {
  @include heading-3;
  @include vertical-margin;
}

.priceContentWrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: $space-400;

  > p {
    @include vertical-margin;
  }
}

.price {
  @include vertical-margin;
  @include body-1;

  & {
    line-height: $space-200;
  }

  @include grid-media-query(sm) {
    @include body-2;

    & {
      font-size: 1.5rem;
      line-height: $space-150;
    }
  }
}

.rangeBar {
  width: 100%;
  height: 6px;
  margin-top: $space-50;
  margin-bottom: $space-100;
  background-color: $color-grey-50;
  border-radius: $border-radius;
}

.rangeBarInner {
  width: 80%;
  height: 100%;
  margin: 0 auto;
  background-color: $color-blue-60;
  border-radius: $border-radius;
}

.rangeTextWrapper {
  display: flex;
  justify-content: space-between;
  width: 100%;

  @include body-2;
}

.date {
  @include caption($color-grey-90);
}
</style>
